// src/App.js — DW Pulse Main Application

import React, { useState, useEffect } from 'react';
import axios from 'axios';
const SalesSummaryPage = React.lazy(() => import('./pages/SalesSummaryPage'));
const ProductMixPage = React.lazy(() => import('./pages/ProductMixPage'));
const SalesForecastPage = React.lazy(() => import('./pages/SalesForecastPage'));
import PerformancePage, { AddPerformanceNoteModal } from './pages/PerformancePage';
import LeaderboardPage, { LeaderboardTV } from './pages/LeaderboardPage';
const SalesProcessorPage = React.lazy(() => import('./pages/SalesProcessorPage'));
// Sales Data, Commissions, and Management are now single tabbed container
// pages (see SalesDataPage.js / CommissionsPage.js / ManagementPage.js) --
// each imports its own former dropdown-child pages directly, so those
// pages are no longer imported here individually.
const SalesDataPage = React.lazy(() => import('./pages/SalesDataPage'));
const CommissionsPage = React.lazy(() => import('./pages/CommissionsPage'));
const ManagementPage = React.lazy(() => import('./pages/ManagementPage'));
const AnnouncementsPage = React.lazy(() => import('./pages/AnnouncementsPage'));
const AppraisalsPage = React.lazy(() => import('./pages/AppraisalsPage'));
const TodoListPage = React.lazy(() => import('./pages/TodoListPage'));
const InventoryPage = React.lazy(() => import('./pages/InventoryPage'));
const AwardsPage = React.lazy(() => import('./pages/AwardsPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const StockCheckPage = React.lazy(() => import('./pages/StockCheckPage'));
const GreenfieldPage = React.lazy(() => import('./pages/GreenfieldPage'));
import MobileNav from './components/MobileNav';
import useIsMobile from './hooks/useIsMobile';
import useIsTablet from './hooks/useIsTablet';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import toast, { Toaster } from 'react-hot-toast';

// A page-level render crash previously took down the entire app with no way
// back — the sidebar nav is part of the same React tree as the page content,
// so when a page threw, the nav died with it. This boundary sits around the
// routed page content only (inside AppShell, wrapping the inner <Routes>),
// so the sidebar and its navigation links stay alive and clickable no
// matter what a specific page does — "stuck on a broken screen with no way
// out" becomes "this one page broke, click something else in the nav."
// Also surfaces the actual error on screen (and via toast), rather than a
// blank/frozen page, so whatever's wrong is visible immediately instead of
// requiring a repro session to even see what broke.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error('Page crashed:', error, info?.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#0F172A', marginBottom: 6 }}>This page hit a problem</div>
          <div style={{ fontSize: 13, color: '#475569', marginBottom: 4, maxWidth: 560, marginLeft: 'auto', marginRight: 'auto' }}>
            Use the menu to go to another page, or try reloading. The error below is useful if you're reporting this.
          </div>
          <div style={{ fontSize: 12, color: '#94A3B8', fontFamily: 'monospace', marginTop: 14, marginBottom: 18, maxWidth: 640, marginLeft: 'auto', marginRight: 'auto', wordBreak: 'break-word' }}>
            {String(this.state.error?.message || this.state.error)}
          </div>
          <button onClick={() => this.setState({ error: null })}
            style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: '#2563EB', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─────────────────────────────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────────────────────────────
const C = {
  blue:       '#2563EB',
  blueDark:   '#1E40AF',
  blueLight:  '#EFF6FF',
  teal:       '#06B6D4',
  tealLight:  '#ECFEFF',
  red:        '#E4002B',
  redLight:   '#FFF1F2',
  white:      '#FFFFFF',
  gray50:     '#F8FAFC',
  gray100:    '#F1F5F9',
  gray200:    '#E2E8F0',
  gray400:    '#94A3B8',
  gray600:    '#475569',
  gray800:    '#1E293B',
  gray900:    '#0F172A',
  success:    '#10B981',
  warning:    '#F59E0B',
  danger:     '#EF4444',
  // Nav accent colors — one per menu section, so the sidebar is quick to
  // scan by color rather than everything reading as the same gray/white.
  purple:     '#7C3AED',
  indigo:     '#4F46E5',
  pink:       '#EC4899',
  gold:       '#EAB308',
  sky:        '#0EA5E9',
  green:      '#16A34A',
  rose:       '#F43F5E',
  orange:     '#EA580C',
};

// Role display helpers
const ROLE_LABELS = {
  super_admin:          'Super Admin',
  admin:                'Admin',
  senior_manager:       'Admin', // legacy alias
  hr_assistant:         'HR Assistant',
  inventory_user:       'Inventory User',
  team_lead:            'Team Lead',
  supervisor:           'Store Supervisor',
  assistant_supervisor: 'Assistant Supervisor',
  csr:                  'CSR',
  employee:             'CSR', // legacy alias
};

const ROLE_COLORS = {
  super_admin:          '#7C3AED',
  admin:                C.red,
  senior_manager:       C.red,
  hr_assistant:         C.sky,
  inventory_user:       C.green,
  team_lead:            C.blue,
  supervisor:           C.teal,
  assistant_supervisor: '#D97706',
  csr:                  C.gray600,
  employee:              C.gray600,
};

// ─────────────────────────────────────────────────────────────────────
// ICONS (inline SVG — no dependency)
// ─────────────────────────────────────────────────────────────────────
const Icon = {
  chevronRight: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  ),
  dashboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
      <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
    </svg>
  ),
  chart: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/>
    </svg>
  ),
  trendingUp: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
    </svg>
  ),
  store: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  dollar: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  ),
  upload: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
    </svg>
  ),
  person: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  clipboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
    </svg>
  ),
  box: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
      <line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
  ),
  camera: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  logout: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
  eye: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ),
  eyeOff: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  ),
  menu: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
  ),
  x: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  map: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
      <line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/>
    </svg>
  ),
  megaphone: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 11l18-7v16l-18-7z"/>
      <path d="M3 11v6a2 2 0 0 0 2 2h1"/>
      <path d="M9 19l1 3h2"/>
    </svg>
  ),
};

// ─────────────────────────────────────────────────────────────────────
// GLOBAL STYLES
// ─────────────────────────────────────────────────────────────────────
const globalStyles = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { font-size: 16px; -webkit-text-size-adjust: 100%; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    background: ${C.gray50};
    color: ${C.gray800};
    line-height: 1.5;
    min-height: 100vh;
  }
  button { cursor: pointer; font-family: inherit; }
  input, select, textarea { font-family: inherit; }
  a { text-decoration: none; color: inherit; }
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: ${C.gray100}; }
  ::-webkit-scrollbar-thumb { background: ${C.gray400}; border-radius: 3px; }
  @media (max-width: 768px) {
    html { font-size: 15px; }
    .dw-page-content { padding-top: 12px !important; padding-left: 8px !important; padding-right: 8px !important; }
  }
`;

// ─────────────────────────────────────────────────────────────────────
// LOGIN PAGE
// ─────────────────────────────────────────────────────────────────────
function LoginPage() {
  const { login } = useAuth();
  const navigate  = useNavigate();

  const [username,    setUsername]    = useState('');
  const [password,    setPassword]    = useState('');
  const [showPass,    setShowPass]    = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [hasCustomBg, setHasCustomBg] = useState(false);

  useEffect(() => {
    // Cheap existence check before committing to the (larger) image request —
    // a 204 means "no custom background", so we just keep the default gradient.
    fetch('/api/settings/login-background', { method: 'GET' })
      .then(res => setHasCustomBg(res.status === 200))
      .catch(() => setHasCustomBg(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!username.trim() || !password) {
      setError('Please enter your username and password');
      return;
    }
    setLoading(true);
    try {
      const user = await login(username.trim(), password);
      toast.success(`Welcome back, ${user.fullName.split(' ')[0]}!`);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      ...styles.loginWrap,
      ...(hasCustomBg ? {
        backgroundImage: 'url(/api/settings/login-background)',
        backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
      } : {}),
    }}>
      {/* Background gradient / overlay */}
      <div style={hasCustomBg ? styles.loginBgOverlay : styles.loginBg} />

      <div style={styles.loginCard}>
        {/* Logo + Brand */}
        <div style={styles.loginHeader}>
          <div style={styles.loginLogo}>
            <img src="/dw-pulse-icon.svg" alt="DW Pulse" style={{ width: 64, height: 64, borderRadius: 14 }} />
            <div style={{ textAlign: 'left' }}>
              <div style={styles.loginLogoDW}>DW<span style={styles.loginLogoPulse}>Pulse</span></div>
            </div>
          </div>
          <p style={styles.loginSubtitle}>DIGIWIRELESS Sales Operations Portal</p>
          <div style={styles.loginDivider} />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.loginForm}>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Username</label>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder="e.g. av5"
              style={styles.formInput}
              autoComplete="username"
              autoFocus
              disabled={loading}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter your password"
                style={{ ...styles.formInput, paddingRight: '44px' }}
                autoComplete="current-password"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPass(p => !p)}
                style={styles.eyeBtn}
              >
                <span style={{ width: 18, height: 18, display: 'block', color: C.gray400 }}>
                  {showPass ? Icon.eyeOff : Icon.eye}
                </span>
              </button>
            </div>
          </div>

          {error && (
            <div style={styles.loginError}>{error}</div>
          )}

          <button
            type="submit"
            style={{ ...styles.loginBtn, opacity: loading ? 0.7 : 1 }}
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p style={styles.loginFooter}>
          Forgot your password? Contact your manager to reset it.
        </p>

        {/* Footer credit */}
        <div style={styles.poweredBy}>
          <span style={{ color: C.gray400, fontSize: 11 }}>Designed and Built by </span>
          <span style={{ color: C.blue, fontSize: 11, fontWeight: 700 }}>Avinash Singh</span>
          <span style={{ color: C.gray400, fontSize: 11 }}> — AVNexus CRM Platform</span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// SIDEBAR NAVIGATION
// ─────────────────────────────────────────────────────────────────────
// Navigation items -- shared between the desktop Sidebar and the mobile
// bottom nav, so there's exactly one place this list is defined.
function useNavItems() {
  const { isSeniorManager, isTeamLeadPlus, isSupervisorPlus, isSuperAdmin, can, hasGreenfieldAccess } = useAuth();

  // Navigation — a handful of frequently-used items stay top-level; the
  // rest are grouped into submenus by function, so the sidebar reads as a
  // short list instead of a long flat stack.
  const navItems = [
    {
      label: 'This Week',
      path: '/this-week',
      icon: Icon.dashboard,
      accent: C.blue,
      show: isSeniorManager,
    },
    {
      label: 'Announcements',
      path: '/announcements',
      icon: Icon.megaphone,
      accent: C.teal,
      show: true,
      quickAccess: true,
    },
    {
      label: 'Sales Summary',
      path: '/sales',
      icon: Icon.chart,
      accent: C.blue,
      show: can('sales_summary.access') !== 'hidden',
      mobilePriority: true,
      quickAccess: true,
    },
    {
      label: 'Sales Forecast',
      path: '/sales-forecast',
      icon: Icon.trendingUp,
      accent: C.orange,
      show: can('sales_forecast.access') !== 'hidden',
    },
    {
      label: 'Product Mix',
      path: '/product-mix',
      icon: Icon.box,
      accent: C.teal,
      show: can('product_mix.access') !== 'hidden',
    },
    {
      label: 'Performance Management',
      path: '/stores',
      icon: Icon.store,
      accent: C.purple,
      show: can('performance.access') !== 'hidden',
    },
    {
      label: 'Sales Processor',
      path: '/sales-processor',
      icon: Icon.upload,
      accent: C.warning,
      show: can('sales_processor.access') === 'true',
      mobilePriority: true,
      quickAccess: true,
    },
    {
      label: 'Leaderboard',
      path: '/leaderboard',
      icon: Icon.trophy || Icon.store,
      accent: C.gold,
      show: can('leaderboard.access') !== 'hidden',
      mobilePriority: true,
      quickAccess: true,
    },
    {
      label: 'Sales Data',
      path: '/sales-data',
      icon: Icon.upload,
      accent: C.sky,
      show: can('sales_data.upload_report') === 'true' || can('sales_data.set_targets') !== 'none' || can('sales_data.reclassification_rules') === 'true' || can('sales_data.manage_uploads') === 'true',
    },
    {
      label: 'Commissions',
      path: '/commissions',
      icon: Icon.dollar,
      accent: C.success,
      show: true,
    },
    {
      label: 'Appraisals',
      path: '/appraisals',
      icon: Icon.users,
      accent: C.indigo,
      show: true,
    },
    {
      label: 'To-Do List',
      path: '/todos',
      icon: Icon.clipboard,
      accent: C.pink,
      show: true,
      quickAccess: true,
    },
    {
      label: 'Inventory',
      path: '/inventory',
      icon: Icon.box,
      accent: C.green,
      show: can('inventory.access') === 'true',
      mobilePriority: true,
      quickAccess: true,
    },
    {
      label: 'Awards',
      path: '/awards',
      icon: Icon.trophy || Icon.store,
      accent: C.gold,
      show: can('awards.access') === 'true',
    },
    {
      label: 'Stock Check',
      path: '/stock-check',
      icon: Icon.camera,
      accent: C.rose,
      show: can('inventory.stock_check') === 'true',
    },
    {
      label: 'Management',
      path: '/management',
      icon: Icon.users,
      accent: C.gray600,
      show: isSupervisorPlus,
    },
    {
      label: 'Greenfield',
      path: '/greenfield',
      icon: Icon.trendingUp,
      accent: C.green,
      // Per-user, not per-role -- see users.greenfield_access. Most
      // accounts will never see this at all, which is the point.
      show: hasGreenfieldAccess,
    },
    {
      label: 'Settings',
      path: '/settings',
      icon: Icon.settings,
      accent: C.gray600,
      show: true,
    },
  ]
    .filter(item => item.show)
    .map(item => item.children ? { ...item, children: item.children.filter(c => c.show) } : item)
    .filter(item => !item.children || item.children.length > 0);

  return navItems;
}

function Sidebar({ isOpen, onClose, isTablet }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const navItems = useNavItems();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    toast.success('You have been logged out');
  };

  // A group auto-expands when the current route lives inside it, so
  // navigating to a nested page doesn't leave the sidebar looking like
  // nothing is selected.
  const [manuallyToggled, setManuallyToggled] = useState({});
  const activeGroupLabel = navItems.find(item => item.children?.some(c => c.path === location.pathname))?.label;
  const isGroupExpanded = (label) => manuallyToggled[label] !== undefined ? manuallyToggled[label] : label === activeGroupLabel;
  const toggleGroup = (label) => setManuallyToggled(prev => ({ ...prev, [label]: !isGroupExpanded(label) }));

  return (
    <>
      <aside style={{ ...styles.sidebar, width: isTablet ? 210 : styles.sidebar.width, transform: isOpen ? 'translateX(0)' : 'translateX(-100%)' }}>
        {/* Brand header */}
        <div style={styles.sidebarHeader}>
          <div style={styles.sidebarBrand}>
            <span style={styles.sidebarBrandDW}>DW</span>
            <span style={styles.sidebarBrandPulse}>PULSE</span>
          </div>
          <button style={styles.sidebarCloseBtn} onClick={onClose}>
            <span style={{ width: 18, height: 18, display: 'block' }}>{Icon.x}</span>
          </button>
        </div>

        {/* User info pill */}
        <div style={styles.sidebarUser}>
          <div style={styles.sidebarAvatar}>
            {user?.fullName?.charAt(0) || 'U'}
          </div>
          <div style={styles.sidebarUserInfo}>
            <div style={styles.sidebarUserName}>{user?.fullName}</div>
            <div style={{
              ...styles.sidebarUserRole,
              color: ROLE_COLORS[user?.role] || C.gray400
            }}>
              {ROLE_LABELS[user?.role] || user?.role}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav style={styles.sidebarNav}>
          {navItems.map(item => item.children ? (
            <div key={item.label}>
              <button
                onClick={() => toggleGroup(item.label)}
                style={{
                  ...styles.navItem, width: '100%', border: 'none', cursor: 'pointer',
                  background: isGroupExpanded(item.label) ? 'rgba(255,255,255,0.05)' : 'transparent',
                  justifyContent: 'space-between',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{
                    width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: `${item.accent}26`, flexShrink: 0,
                  }}>
                    <span style={{ width: 16, height: 16, display: 'block', color: item.accent }}>{item.icon}</span>
                  </span>
                  <span style={styles.navLabel}>{item.label}</span>
                </span>
                <span style={{
                  width: 14, height: 14, display: 'block', flexShrink: 0,
                  transform: isGroupExpanded(item.label) ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.15s',
                }}>
                  {Icon.chevronRight || '›'}
                </span>
              </button>
              {isGroupExpanded(item.label) && (
                <div style={{ paddingLeft: 14, borderLeft: `1px solid ${item.accent}33`, marginLeft: 22 }}>
                  {item.children.map(child => (
                    <NavLink
                      key={child.path}
                      to={child.path}
                      onClick={onClose}
                      style={({ isActive }) => ({
                        ...styles.navItem, ...(isActive ? styles.navItemActive(item.accent) : {}),
                        padding: '8px 12px', fontSize: 13,
                      })}
                    >
                      <span style={styles.navLabel}>{child.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onClose}
              style={({ isActive }) => ({
                ...styles.navItem,
                ...(isActive ? styles.navItemActive(item.accent) : {}),
              })}
            >
              <span style={{
                width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `${item.accent}26`, flexShrink: 0,
              }}>
                <span style={{ width: 16, height: 16, display: 'block', color: item.accent }}>
                  {item.icon}
                </span>
              </span>
              <span style={styles.navLabel}>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Theme toggle + Logout */}
        <div style={styles.sidebarFooter}>
          <div style={styles.sidebarFooterDivider} />
          <button
            onClick={toggleTheme}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
              padding: '9px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.05)', border: 'none',
              color: 'rgba(255,255,255,0.75)', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 6,
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>{isDark ? '🌙' : '☀️'}</span> {isDark ? 'Dark Mode' : 'Light Mode'}
            </span>
            <span style={{
              width: 34, height: 18, borderRadius: 10, background: isDark ? '#2563EB' : 'rgba(255,255,255,0.2)',
              position: 'relative', transition: 'background 0.2s', flexShrink: 0,
            }}>
              <span style={{
                position: 'absolute', top: 2, left: isDark ? 18 : 2, width: 14, height: 14, borderRadius: '50%',
                background: '#fff', transition: 'left 0.2s',
              }} />
            </span>
          </button>
          <button style={styles.logoutBtn} onClick={handleLogout}>
            <span style={{ width: 16, height: 16, display: 'block' }}>{Icon.logout}</span>
            <span>Sign Out</span>
          </button>
          <p style={styles.sidebarVersion}>DW Pulse v1.0 · avnexus.app</p>
        </div>
      </aside>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// MAIN LAYOUT SHELL
// ─────────────────────────────────────────────────────────────────────
// Desktop quick-access bar -- previously drove off the same flag as the
// mobile bottom tab bar; now its own quickAccess flag, decoupled from
// mobilePriority, so trimming the mobile bottom bar (a phone screen fits
// far fewer tabs comfortably than this slim desktop strip can) doesn't
// also change what shows up here. Sits alongside the sidebar, doesn't
// replace it -- exploratory, built as a slim strip so it doesn't compete
// with the sidebar for the primary navigation role.
function DesktopQuickAccessBar({ isDark }) {
  const location = useLocation();
  const navItems = useNavItems();
  const priorityItems = navItems.filter(i => i.show && i.quickAccess && !i.children);
  if (priorityItems.length === 0) return null;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, padding: '8px 20px',
      background: isDark ? '#1E3A5F' : C.blueLight,
      borderBottom: `2px solid ${isDark ? C.blueDark : C.blue}`,
      overflowX: 'auto',
    }}>
      <span style={{ fontSize: 11, fontWeight: 800, color: isDark ? '#93C5FD' : C.blueDark, textTransform: 'uppercase', letterSpacing: '0.3px', marginRight: 4, flexShrink: 0 }}>Quick access</span>
      {priorityItems.map(item => {
        const isActive = item.path === location.pathname;
        return (
          <NavLink key={item.path} to={item.path}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8,
              textDecoration: 'none', fontSize: 13, fontWeight: 700, flexShrink: 0,
              background: isActive ? `${item.accent}18` : (isDark ? '#1E293B' : C.white),
              color: isActive ? item.accent : (isDark ? '#CBD5E1' : C.gray600),
              border: `1px solid ${isActive ? item.accent : (isDark ? '#334155' : C.gray200)}`,
            }}>
            <span style={{ width: 16, height: 16, display: 'block' }}>{item.icon}</span>
            {item.label}
          </NavLink>
        );
      })}
      <span style={{ width: 1, alignSelf: 'stretch', background: isDark ? '#334155' : C.gray200, flexShrink: 0, margin: '0 2px' }} />
      <QuickNoteButton isDark={isDark} variant="inline" />
    </div>
  );
}

// Floating quick-action button for logging a Kudos/Coaching note without
// first navigating into Performance Management and finding the person's
// row there. Same permission gate (appraisals.edit) and the exact same
// modal that feature already uses -- this is just a faster way in, not a
// separate note-taking system. Visible on both mobile and desktop, fixed
// position so it's reachable from any page.
function QuickNoteButton({ isDark, variant = 'floating' }) {
  const { can } = useAuth();
  const canAddNote = can('appraisals.edit') === 'true';
  const isMobile = useIsMobile();
  const [showPicker, setShowPicker] = useState(false);
  const [employees, setEmployees] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);

  // Fade/shrink the floating button while the user is actively scrolling
  // down through page content, and bring it back the moment they stop or
  // reverse direction -- so it's out of the way exactly when it would
  // otherwise sit over whatever they're reading, without disappearing
  // for good or needing an extra tap to reveal it again.
  const [receding, setReceding] = useState(false);
  useEffect(() => {
    if (variant !== 'floating') return;
    const el = document.querySelector('.dw-page-content');
    if (!el) return;
    let lastTop = el.scrollTop;
    let stopTimer = null;
    const onScroll = () => {
      const top = el.scrollTop;
      const goingDown = top > lastTop + 2;
      const goingUp = top < lastTop - 2;
      lastTop = top;
      if (goingDown) setReceding(true);
      else if (goingUp) setReceding(false);
      if (stopTimer) clearTimeout(stopTimer);
      stopTimer = setTimeout(() => setReceding(false), 500);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); if (stopTimer) clearTimeout(stopTimer); };
  }, [variant]);

  if (!canAddNote) return null;

  const openPicker = () => {
    setShowPicker(true);
    setSearch('');
    if (!employees) {
      axios.get('/api/appraisals/employees').then(r => setEmployees(r.data)).catch(() => setEmployees([]));
    }
  };

  const filtered = (employees || []).filter(e =>
    (e.linked_staff_name || e.full_name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      {variant === 'floating' ? (
        <button onClick={openPicker} title="Log a Kudos or Coaching note"
          style={{
            position: 'fixed', right: 14, zIndex: 45,
            // 66px clears MobileNav's base height with a bit of margin;
            // env(safe-area-inset-bottom) adds however much MORE MobileNav
            // grows by on a PWA with a home-indicator strip (see its own
            // paddingBottom using the same value) -- without this, the
            // button stayed pinned at a fixed 66px while the nav bar grew
            // taller underneath it, so it ended up sitting low enough to
            // overlap the bar instead of floating above it.
            bottom: isMobile ? 'calc(66px + env(safe-area-inset-bottom, 0px))' : 20,
            width: 46, height: 46, borderRadius: '50%', border: 'none', cursor: 'pointer',
            background: '#16A34A', color: C.white, fontSize: 19,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(22,163,74,0.4)',
            opacity: receding ? 0.28 : 1, transform: receding ? 'scale(0.8)' : 'scale(1)',
            transition: 'opacity 0.2s ease, transform 0.2s ease',
            pointerEvents: receding ? 'none' : 'auto',
          }}>
          ⭐
        </button>
      ) : (
        <button onClick={openPicker} title="Log a Kudos or Coaching note"
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8,
            fontSize: 13, fontWeight: 700, flexShrink: 0, cursor: 'pointer',
            background: isDark ? 'rgba(22,163,74,0.18)' : '#F0FDF4',
            color: isDark ? '#4ADE80' : '#16A34A',
            border: `1px solid ${isDark ? '#4ADE80' : '#16A34A'}`,
          }}>
          <span style={{ width: 16, height: 16, display: 'block' }}>⭐</span>
          Kudos / Note
        </button>
      )}

      {showPicker && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowPicker(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.white, borderRadius: 16, padding: 20, width: 380, maxWidth: '92%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.gray900, marginBottom: 10 }}>⭐ Who is this for?</div>
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name…"
              style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1.5px solid ${C.gray200}`, fontSize: 16, boxSizing: 'border-box', marginBottom: 10 }} />
            <div style={{ overflowY: 'auto', flex: 1, minHeight: 100 }}>
              {employees === null && <div style={{ textAlign: 'center', color: C.gray400, fontSize: 13, padding: 20 }}>Loading…</div>}
              {employees !== null && filtered.length === 0 && <div style={{ textAlign: 'center', color: C.gray400, fontSize: 13, padding: 20 }}>No matches</div>}
              {filtered.map(e => (
                <button key={e.id} onClick={() => { setSelectedAgent(e); setShowPicker(false); }}
                  style={{ width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13.5, color: C.gray900, fontWeight: 600 }}>
                  {e.linked_staff_name || e.full_name}
                  {e.store_code && <span style={{ color: C.gray400, fontWeight: 500 }}> · {e.store_code}</span>}
                </button>
              ))}
            </div>
            <button onClick={() => setShowPicker(false)}
              style={{ marginTop: 10, padding: '8px 16px', borderRadius: 8, border: `1.5px solid ${C.gray200}`, background: C.white, color: C.gray700, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {selectedAgent && (
        <AddPerformanceNoteModal
          agentName={selectedAgent.linked_staff_name || selectedAgent.full_name}
          storeCode={selectedAgent.store_code}
          onClose={() => setSelectedAgent(null)}
        />
      )}
    </>
  );
}

function AppShell({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user } = useAuth();
  const { isDark } = useTheme();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const navItems = useNavItems();
  const sidebarWidth = isTablet ? 210 : 260;

  return (
    <div style={styles.appShell}>
      {!isMobile && <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isTablet={isTablet} />}

      <div style={{
        ...styles.mainArea,
        marginLeft: (!isMobile && sidebarOpen) ? sidebarWidth : 0,
        transition: 'margin-left 0.25s ease',
        background: isDark ? '#0B1220' : C.gray50,
      }}>
        {/* Top bar */}
        <header style={{ ...styles.topBar, background: isDark ? '#111827' : C.white, borderBottom: `1px solid ${isDark ? '#1F2937' : C.gray200}` }}>
          {!isMobile && (
            <button
              style={{ ...styles.menuBtn, color: isDark ? '#CBD5E1' : C.gray600 }}
              onClick={() => setSidebarOpen(o => !o)}
            >
              <span style={{ width: 22, height: 22, display: 'block' }}>{Icon.menu}</span>
            </button>
          )}
          {isMobile && (
            <NavLink to="/announcements" style={{ ...styles.sidebarBrand, textDecoration: 'none' }}>
              <span style={{ ...styles.sidebarBrandDW, color: isDark ? C.white : C.gray900 }}>DW</span>
              <span style={styles.sidebarBrandPulse}>PULSE</span>
            </NavLink>
          )}

          <div style={styles.topBarRight}>
            <div style={styles.topBarUser}>
              <span style={{ ...styles.topBarName, color: isDark ? '#E2E8F0' : C.gray800 }}>{user?.fullName?.split(' ')[0]}</span>
              <div style={styles.topBarAvatar}>
                {user?.fullName?.charAt(0) || 'U'}
              </div>
            </div>
          </div>
        </header>

        {!isMobile && <DesktopQuickAccessBar isDark={isDark} />}

        {/* Page content -- extra bottom padding on mobile so the fixed
            bottom tab bar AND the floating Kudos button (which sits above
            it) never cover the last bit of page content. The button's top
            edge is around 112px up from the screen bottom (66px offset +
            46px tall), so this needs real headroom past the nav bar alone. */}
        <main className="dw-page-content" style={{ ...styles.pageContent, padding: isTablet ? '20px 18px' : styles.pageContent.padding, paddingBottom: isMobile ? 128 : styles.pageContent.paddingBottom }}>
          {children}
        </main>
      </div>

      {isMobile && <MobileNav navItems={navItems} isDark={isDark} />}
      {isMobile && <QuickNoteButton isDark={isDark} variant="floating" />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// PLACEHOLDER PAGES (Phase 2+ will fill these)
// ─────────────────────────────────────────────────────────────────────
function ComingSoonPage({ title, icon }) {
  return (
    <div style={styles.comingSoon}>
      <div style={styles.comingSoonIcon}>{icon}</div>
      <h2 style={styles.comingSoonTitle}>{title}</h2>
      <p style={styles.comingSoonText}>
        This module is being built and will be available in the next phase.
      </p>
      <div style={styles.comingSoonBadge}>Coming in Phase 2</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// CHANGE PASSWORD PAGE
// ─────────────────────────────────────────────────────────────────────
function SettingsPage() {
  const { changePassword, can } = useAuth();
  const [cur,    setCur]    = useState('');
  const [next,   setNext]   = useState('');
  const [conf,   setConf]   = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next !== conf) { toast.error('New passwords do not match'); return; }
    if (next.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    setSaving(true);
    try {
      await changePassword(cur, next);
      toast.success('Password changed successfully');
      setCur(''); setNext(''); setConf('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Settings</h1>
          <p style={styles.pageSubtitle}>Manage your account preferences</p>
        </div>
      </div>

      <div style={styles.settingsCard}>
        <h3 style={styles.settingsCardTitle}>Change Password</h3>
        <form onSubmit={handleSubmit} style={{ maxWidth: 400 }}>
          {[
            { label: 'Current Password', val: cur, set: setCur },
            { label: 'New Password',     val: next, set: setNext },
            { label: 'Confirm New Password', val: conf, set: setConf },
          ].map(({ label, val, set }) => (
            <div key={label} style={styles.formGroup}>
              <label style={styles.formLabel}>{label}</label>
              <input
                type="password"
                value={val}
                onChange={e => set(e.target.value)}
                style={styles.formInput}
                disabled={saving}
              />
            </div>
          ))}
          <button
            type="submit"
            style={{ ...styles.loginBtn, marginTop: 8 }}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Update Password'}
          </button>
        </form>
      </div>

      {can('settings.reset_password_scope') === 'own_team' && <TeamPasswordReset />}
      {can('login_screen.change_background') === 'true' && <LoginBrandingSettings />}
    </div>
  );
}

function TeamPasswordReset() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState('');
  const [newPw, setNewPw] = useState('');
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    axios.get('/api/users/my-team').then(r => setTeam(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleReset = async (e) => {
    e.preventDefault();
    if (!selectedId) { toast.error('Choose a team member'); return; }
    if (newPw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setResetting(true);
    try {
      await axios.post(`/api/users/${selectedId}/reset-password`, { newPassword: newPw });
      toast.success('Password reset for that team member');
      setSelectedId(''); setNewPw('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div style={styles.settingsCard}>
      <h3 style={styles.settingsCardTitle}>Reset a Team Member's Password</h3>
      {loading ? (
        <p style={styles.pageSubtitle}>Loading your team…</p>
      ) : team.length === 0 ? (
        <p style={styles.pageSubtitle}>No team members found for your assigned store(s).</p>
      ) : (
        <form onSubmit={handleReset} style={{ maxWidth: 400 }}>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Team Member</label>
            <select value={selectedId} onChange={e => setSelectedId(e.target.value)} style={styles.formInput} disabled={resetting}>
              <option value="">Select someone…</option>
              {team.map(u => <option key={u.id} value={u.id}>{u.full_name}{u.store_code ? ` (${u.store_code.replace('DIGIWIRE', 'DW')})` : ''}</option>)}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>New Password</label>
            <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} style={styles.formInput} disabled={resetting} />
          </div>
          <button type="submit" style={{ ...styles.loginBtn, marginTop: 8 }} disabled={resetting}>
            {resetting ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      )}
    </div>
  );
}

function LoginBrandingSettings() {
  const [status, setStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileInputRef = React.useRef(null);

  const loadStatus = () => {
    axios.get('/api/settings/login-background/status').then(r => setStatus(r.data)).catch(() => {});
  };
  useEffect(() => { loadStatus(); }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    upload(file);
  };

  const upload = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      await axios.post('/api/settings/login-background', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Login background updated');
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to upload background');
    } finally {
      setUploading(false);
    }
  };

  const resetToDefault = async () => {
    if (!window.confirm('Remove the custom login background and go back to the default gradient?')) return;
    try {
      await axios.delete('/api/settings/login-background');
      toast.success('Reset to default background');
      setPreview(null);
      loadStatus();
    } catch (err) { toast.error('Failed to reset background'); }
  };

  return (
    <div style={{ ...styles.settingsCard, marginTop: 20 }}>
      <h3 style={styles.settingsCardTitle}>Login Screen Background</h3>
      <p style={{ fontSize: 13, color: C.gray600, marginBottom: 16, maxWidth: 480 }}>
        Upload an image to use as the login screen background. Replace it any time — the previous one is overwritten. Leave unset to use the default gradient.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
        <div style={{
          width: 140, height: 88, borderRadius: 10, overflow: 'hidden',
          background: preview ? `url(${preview})` : (status?.hasCustomBackground ? 'url(/api/settings/login-background)' : `linear-gradient(135deg, ${C.blueDark}, ${C.blue}, ${C.teal})`),
          backgroundSize: 'cover', backgroundPosition: 'center',
          border: `1.5px solid ${C.gray200}`, flexShrink: 0,
        }} />
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.gray800 }}>
            {status?.hasCustomBackground ? 'Custom background active' : 'Using default gradient'}
          </div>
          {status?.updatedAt && (
            <div style={{ fontSize: 11, color: C.gray400, marginTop: 2 }}>Last updated {status.updatedAt}</div>
          )}
        </div>
      </div>

      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} style={{ display: 'none' }} />
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: `linear-gradient(135deg,${C.blue},${C.teal})`, color: C.white, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
        >
          {uploading ? 'Uploading...' : '📤 Upload New Image'}
        </button>
        {status?.hasCustomBackground && (
          <button onClick={resetToDefault} style={{ padding: '9px 18px', borderRadius: 8, border: `1px solid ${C.gray200}`, background: C.white, color: C.gray600, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            Reset to Default
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ROUTE GUARD
// ─────────────────────────────────────────────────────────────────────
function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div style={styles.loadingScreen}>
        <div style={styles.loadingSpinner} />
        <p style={{ color: C.gray400, marginTop: 16 }}>Loading DW Pulse...</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// ─────────────────────────────────────────────────────────────────────
// APP ROOT
// ─────────────────────────────────────────────────────────────────────
function AuthedAppRoutes() {
  const location = useLocation();
  return (
    <RequireAuth>
      <AppShell>
        <ErrorBoundary key={location.pathname}>
          <React.Suspense fallback={
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', color: C.gray400, fontSize: 14, fontWeight: 600 }}>
              Loading…
            </div>
          }>
          <Routes>
            <Route path="/announcements" element={<AnnouncementsPage />} />
            <Route path="/sales"       element={<SalesSummaryPage />} />
            <Route path="/sales-forecast" element={<SalesForecastPage />} />
            <Route path="/product-mix" element={<ProductMixPage />} />
            <Route path="/stores"      element={<PerformancePage />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/sales-processor" element={<SalesProcessorPage />} />
            <Route path="/sales-data"  element={<SalesDataPage />} />
            <Route path="/scorecards"  element={<PerformancePage />} />
            <Route path="/commissions"  element={<CommissionsPage />} />
            <Route path="/appraisals"  element={<AppraisalsPage />} />
            <Route path="/todos"       element={<TodoListPage />} />
            <Route path="/inventory"   element={<InventoryPage />} />
            <Route path="/awards"      element={<AwardsPage />} />
            <Route path="/transfers"   element={<Navigate to="/inventory?tab=transfers" replace />} />
            <Route path="/this-week"   element={<DashboardPage />} />
            <Route path="/stock-check" element={<StockCheckPage />} />
            <Route path="/management"  element={<ManagementPage />} />
            <Route path="/greenfield"  element={<GreenfieldPage />} />
            <Route path="/settings"    element={<SettingsPage />} />
            <Route path="*"            element={<Navigate to="/announcements" replace />} />
          </Routes>
          </React.Suspense>
        </ErrorBoundary>
      </AppShell>
    </RequireAuth>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
        <style>{globalStyles}</style>
        <Toaster
          position="top-right"
          toastOptions={{
            style: { fontFamily: 'inherit', fontSize: 14 },
            success: { iconTheme: { primary: C.success, secondary: '#fff' } },
            // Soft amber, left-border card instead of a hard red circle-X --
            // reads as "heads up" rather than "something crashed," matching
            // the left-border card language already used throughout the app
            // (e.g. the Sales Summary category cards).
            error: {
              iconTheme: { primary: C.warning, secondary: '#fff' },
              style: { background: C.white, border: `1px solid ${C.gray200}`, borderLeft: `4px solid ${C.warning}`, color: C.gray900 },
            },
          }}
        />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/leaderboard/tv" element={
            <RequireAuth><LeaderboardTV /></RequireAuth>
          } />
          <Route path="/*" element={<AuthedAppRoutes />} />
          <Route path="/" element={<Navigate to="/announcements" replace />} />
        </Routes>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

// ─────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────
const styles = {
  // Login
  loginWrap: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: `linear-gradient(135deg, ${C.blueDark} 0%, ${C.blue} 50%, ${C.teal} 100%)`,
    padding: '24px 16px', position: 'relative', overflow: 'hidden',
  },
  loginBg: {
    position: 'absolute', inset: 0,
    background: 'radial-gradient(ellipse at 20% 50%, rgba(255,255,255,0.10) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(255,255,255,0.08) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  loginBgOverlay: {
    position: 'absolute', inset: 0,
    background: 'linear-gradient(135deg, rgba(15,23,42,0.55) 0%, rgba(15,23,42,0.35) 100%)',
    pointerEvents: 'none',
  },
  loginCard: {
    background: C.white, borderRadius: 20, padding: '40px 36px',
    width: '100%', maxWidth: 420, boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
    position: 'relative', zIndex: 1,
  },
  loginHeader: { textAlign: 'center', marginBottom: 28 },
  loginLogo: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginBottom: 10 },
  loginLogoDW: {
    fontSize: 34, fontWeight: 800, color: C.gray900, letterSpacing: '-0.5px',
    lineHeight: 1, fontFamily: `'Segoe UI', system-ui, -apple-system, sans-serif`,
  },
  loginLogoPulse: {
    fontWeight: 800,
    background: `linear-gradient(90deg, ${C.blue}, ${C.teal})`,
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  loginSubtitle: { color: C.gray600, fontSize: 13, marginBottom: 20 },
  loginDivider: { height: 2, background: `linear-gradient(90deg, ${C.blue}, ${C.teal})`, borderRadius: 2 },
  loginForm: { display: 'flex', flexDirection: 'column', gap: 16 },
  formGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  formLabel: { fontSize: 13, fontWeight: 600, color: C.gray800 },
  formInput: {
    padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${C.gray200}`,
    fontSize: 14, outline: 'none', transition: 'border-color 0.2s',
    background: C.gray50, color: C.gray800, width: '100%',
  },
  eyeBtn: {
    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', padding: 0, display: 'flex', cursor: 'pointer',
  },
  loginError: {
    background: C.redLight, color: C.red, borderRadius: 8, padding: '10px 14px',
    fontSize: 13, border: `1px solid ${C.red}20`,
  },
  loginBtn: {
    padding: '12px 24px', borderRadius: 10, border: 'none',
    background: `linear-gradient(135deg, ${C.red}, #900018)`,
    color: C.white, fontWeight: 700, fontSize: 15, cursor: 'pointer',
    transition: 'transform 0.15s, box-shadow 0.15s', marginTop: 4,
    boxShadow: `0 4px 15px ${C.red}50`,
  },
  loginFooter: { textAlign: 'center', fontSize: 12, color: C.gray400, marginTop: 20 },
  poweredBy: { textAlign: 'center', marginTop: 12 },

  // App shell
  appShell: { display: 'flex', minHeight: '100vh' },

  // Sidebar
  sidebarOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    zIndex: 99,
  },
  sidebar: {
    width: 260, background: C.gray900, color: C.white,
    display: 'flex', flexDirection: 'column', flexShrink: 0,
    position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 100,
    overflowY: 'auto', transition: 'transform 0.25s ease',
  },
  sidebarHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '20px 20px 16px',
    // Same iOS PWA status-bar-overlay reasoning as topBar above -- this
    // sidebar is a position:fixed panel pinned to the physical top of the
    // screen (it's the full-screen mobile drawer on small screens), so its
    // header is just as exposed to the status bar as the main topBar is.
    paddingTop: 'calc(20px + env(safe-area-inset-top, 0px))',
    borderBottom: `1px solid rgba(255,255,255,0.08)`,
  },
  sidebarBrand: { display: 'flex', alignItems: 'baseline', gap: 4 },
  sidebarBrandDW: { fontSize: 24, fontWeight: 900, color: C.white, letterSpacing: '-0.5px' },
  sidebarBrandPulse: { fontSize: 14, fontWeight: 700, color: C.teal, letterSpacing: '2px' },
  sidebarCloseBtn: {
    background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 6,
    color: C.white, padding: 6, display: 'flex', cursor: 'pointer',
  },
  sidebarUser: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '16px 20px', borderBottom: `1px solid rgba(255,255,255,0.08)`,
  },
  sidebarAvatar: {
    width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
    background: `linear-gradient(135deg, ${C.blue}, ${C.teal})`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: 16, color: C.white,
  },
  sidebarUserInfo: { flex: 1, minWidth: 0 },
  sidebarUserName: { fontSize: 13, fontWeight: 600, color: C.white, truncate: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' },
  sidebarUserRole: { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 1 },
  sidebarNav: { flex: 1, padding: '12px 12px', display: 'flex', flexDirection: 'column', gap: 2 },
  navItem: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
    borderRadius: 8, color: 'rgba(255,255,255,0.65)', fontSize: 13.5, fontWeight: 500,
    transition: 'all 0.15s', cursor: 'pointer',
  },
  navItemActive: (accent = C.teal) => ({
    background: `linear-gradient(135deg, ${accent}30, ${accent}12)`,
    color: C.white, borderLeft: `3px solid ${accent}`,
    paddingLeft: 9,
  }),
  navLabel: { flex: 1 },
  sidebarFooter: { padding: '0 12px 20px' },
  sidebarFooterDivider: { height: 1, background: 'rgba(255,255,255,0.08)', margin: '0 8px 12px' },
  logoutBtn: {
    display: 'flex', alignItems: 'center', gap: 10,
    width: '100%', padding: '9px 12px', borderRadius: 8,
    background: 'none', border: 'none', color: `${C.red}cc`,
    fontSize: 13.5, fontWeight: 500, cursor: 'pointer',
    transition: 'background 0.15s',
  },
  sidebarVersion: { fontSize: 10, color: 'rgba(255,255,255,0.25)', textAlign: 'center', marginTop: 12 },

  // Main area
  mainArea: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  topBar: {
    minHeight: 60, background: C.white, borderBottom: `1px solid ${C.gray200}`,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 24px', position: 'sticky', top: 0, zIndex: 50,
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    // iOS PWA (Add to Home Screen) renders the status bar as an overlay on
    // top of the page rather than pushing content down -- see
    // apple-mobile-web-app-status-bar-style: black-translucent in
    // index.html, which is what makes that overlay possible in the first
    // place. Without this, the topmost sticky bar on every single page
    // sits partly behind the status bar/notch. env() with a 0px fallback
    // is a no-op on any browser/device that doesn't have a safe-area
    // inset to report (older iOS, Android, desktop), so this is safe
    // everywhere, not just notched iPhones.
    paddingTop: 'env(safe-area-inset-top, 0px)',
  },
  menuBtn: {
    background: 'none', border: 'none', padding: 8, borderRadius: 8,
    color: C.gray600, cursor: 'pointer', display: 'flex',
  },
  topBarRight: { display: 'flex', alignItems: 'center', gap: 12 },
  topBarUser: { display: 'flex', alignItems: 'center', gap: 10 },
  topBarName: { fontSize: 14, fontWeight: 600, color: C.gray800 },
  topBarAvatar: {
    width: 34, height: 34, borderRadius: '50%',
    background: `linear-gradient(135deg, ${C.blue}, ${C.teal})`,
    color: C.white, fontWeight: 700, fontSize: 14,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  pageContent: { flex: 1, padding: '28px 28px', overflowY: 'auto', WebkitOverflowScrolling: 'touch' },

  // Page structure
  pageHeader: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    marginBottom: 24, flexWrap: 'wrap', gap: 12,
  },
  pageTitle: { fontSize: 24, fontWeight: 800, color: C.gray900 },
  pageSubtitle: { fontSize: 14, color: C.gray600, marginTop: 3 },
  pagePeriodBadge: {
    padding: '6px 14px', borderRadius: 20,
    background: C.blueLight, color: C.blue, fontSize: 13, fontWeight: 600,
    border: `1px solid ${C.blue}30`,
  },

  // Welcome banner
  welcomeBanner: {
    background: `linear-gradient(135deg, ${C.blueDark} 0%, ${C.blue} 60%, ${C.teal} 100%)`,
    borderRadius: 16, padding: '24px 28px', marginBottom: 24,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 16, flexWrap: 'wrap', color: C.white,
  },
  welcomeLeft: { flex: 1, minWidth: 200 },
  welcomeGreeting: { fontSize: 20, fontWeight: 700, marginBottom: 6 },
  welcomeDesc: { fontSize: 14, color: 'rgba(255,255,255,0.8)' },
  welcomeRole: {},
  roleBadge: {
    display: 'inline-block', padding: '5px 14px', borderRadius: 20,
    fontSize: 12, fontWeight: 700, border: '1px solid', textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  // Metric cards
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: 16, marginBottom: 24,
  },
  metricCard: {
    background: C.white, borderRadius: 14, padding: '18px 20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: `1px solid ${C.gray200}`,
  },
  metricCardTop: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 },
  metricIcon: { fontSize: 20 },
  metricLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' },
  metricValue: { fontSize: 32, fontWeight: 800, color: C.gray900, lineHeight: 1, marginBottom: 4 },
  metricTarget: { fontSize: 12, color: C.gray400, marginBottom: 10 },
  metricBar: { height: 4, borderRadius: 2, overflow: 'hidden' },
  metricBarFill: { height: '100%', borderRadius: 2, transition: 'width 0.5s ease' },

  // Setup notice
  setupNotice: {
    background: '#FFFBEB', border: `1px solid #FDE68A`, borderRadius: 14,
    padding: '18px 22px', display: 'flex', alignItems: 'flex-start', gap: 14,
    marginBottom: 24,
  },
  setupNoticeIcon: { fontSize: 24, flexShrink: 0 },
  setupNoticeTitle: { fontWeight: 700, color: '#92400E', fontSize: 15, marginBottom: 4 },
  setupNoticeText: { color: '#78350F', fontSize: 13, lineHeight: 1.6 },

  // Quick links
  quickLinks: { marginTop: 4 },
  quickLinksTitle: { fontSize: 16, fontWeight: 700, color: C.gray800, marginBottom: 14 },
  quickLinksGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12,
  },
  quickLinkCard: {
    background: C.white, borderRadius: 12, padding: '16px 18px',
    display: 'flex', alignItems: 'center', gap: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: `1px solid ${C.gray200}`,
    cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s',
  },
  quickLinkIcon: { fontSize: 24 },
  quickLinkLabel: { fontWeight: 700, fontSize: 14, color: C.gray800, marginBottom: 2 },
  quickLinkDesc: { fontSize: 12, color: C.gray600 },

  // Settings
  settingsCard: {
    background: C.white, borderRadius: 16, padding: '28px 32px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: `1px solid ${C.gray200}`,
    maxWidth: 560,
  },
  settingsCardTitle: { fontSize: 16, fontWeight: 700, color: C.gray800, marginBottom: 20 },

  // Coming soon
  comingSoon: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', minHeight: '60vh', textAlign: 'center', gap: 12,
  },
  comingSoonIcon: { marginBottom: 8, opacity: 0.5 },
  comingSoonTitle: { fontSize: 24, fontWeight: 800, color: C.gray800 },
  comingSoonText: { color: C.gray600, fontSize: 15, maxWidth: 360 },
  comingSoonBadge: {
    marginTop: 8, padding: '6px 16px', borderRadius: 20,
    background: C.blueLight, color: C.blue, fontSize: 13, fontWeight: 600,
  },

  // Loading
  loadingScreen: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    background: `linear-gradient(135deg, ${C.blueDark}, ${C.blue})`,
  },
  loadingSpinner: {
    width: 44, height: 44, borderRadius: '50%',
    border: `3px solid rgba(255,255,255,0.2)`,
    borderTopColor: C.teal,
    animation: 'spin 0.9s linear infinite',
  },
};
